import { Box, ToggleButton, ToggleButtonGroup, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Tooltip } from '@mui/material';
import {
  ErrorOutline as MissedIcon,
  ReplayCircleFilled as ReturnIcon,
  HourglassBottom as AgedIcon,
} from '@mui/icons-material';

// Shared ticket filtering used by Tickets, Calendar and Scheduling pages so the
// same controls behave identically everywhere: scheduled date (day / range /
// all), technician (all / unassigned / specific), and the derived flag states
// (missed / needs return visit / aged).

export interface TicketFilterValue {
  dateMode: 'all' | 'day' | 'range';
  day: string;   // YYYY-MM-DD
  from: string;  // YYYY-MM-DD
  to: string;    // YYYY-MM-DD
  tech: 'all' | 'unassigned' | number;
  flags: { missed: boolean; needs_return: boolean; aged: boolean };
}

export interface TechOption { id: number; first_name?: string; last_name?: string; email?: string; name?: string }

export const defaultTicketFilter = (): TicketFilterValue => {
  const today = new Date().toISOString().slice(0, 10);
  return { dateMode: 'all', day: today, from: today, to: today, tech: 'all',
           flags: { missed: false, needs_return: false, aged: false } };
};

const techLabel = (t: TechOption) =>
  (`${t.first_name || ''} ${t.last_name || ''}`).trim() || t.name || t.email || `Tech ${t.id}`;

/** Build query params for ticketsApi.list() from a filter value. */
export function ticketFilterToParams(v: TicketFilterValue): Record<string, any> {
  const p: Record<string, any> = {};
  if (v.dateMode === 'day' && v.day) p.scheduled_day = v.day;
  if (v.dateMode === 'range') {
    if (v.from) p.scheduled_from = `${v.from}T00:00:00`;
    if (v.to) p.scheduled_to = `${v.to}T23:59:59`;
  }
  if (v.tech === 'unassigned') p.unassigned = true;
  else if (typeof v.tech === 'number') p.assigned_to_id = v.tech;
  const fl = Object.entries(v.flags).filter(([, on]) => on).map(([k]) => k);
  if (fl.length) p.flags = fl.join(',');
  return p;
}

/** True if a ticket-like object passes the filter (client-side). */
export function ticketMatchesFilter(t: any, v: TicketFilterValue): boolean {
  if (v.dateMode !== 'all') {
    const ss = t.scheduled_start ? new Date(t.scheduled_start) : null;
    if (!ss) return false;
    const d = ss.toISOString().slice(0, 10);
    if (v.dateMode === 'day' && d !== v.day) return false;
    if (v.dateMode === 'range') {
      if (v.from && d < v.from) return false;
      if (v.to && d > v.to) return false;
    }
  }
  if (v.tech === 'unassigned' && t.assigned_to_id) return false;
  if (typeof v.tech === 'number' && t.assigned_to_id !== v.tech) return false;
  const anyFlag = v.flags.missed || v.flags.needs_return || v.flags.aged;
  if (anyFlag) {
    const isMissed = !!t.missed;
    const isReturn = !!(t.needs_return ?? t.return_trip_required);
    const isAged = !!t.aged;
    const ok = (v.flags.missed && isMissed) || (v.flags.needs_return && isReturn) || (v.flags.aged && isAged);
    if (!ok) return false;
  }
  return true;
}

interface Props {
  value: TicketFilterValue;
  onChange: (v: TicketFilterValue) => void;
  technicians?: TechOption[];
  showTech?: boolean;
  showFlags?: boolean;
  size?: 'small' | 'medium';
}

export default function TicketFilters({ value, onChange, technicians = [], showTech = true, showFlags = true, size = 'small' }: Props) {
  const set = (patch: Partial<TicketFilterValue>) => onChange({ ...value, ...patch });
  const toggleFlag = (k: 'missed' | 'needs_return' | 'aged') =>
    set({ flags: { ...value.flags, [k]: !value.flags[k] } });

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        size={size} exclusive value={value.dateMode}
        onChange={(_, m) => m && set({ dateMode: m })}
        aria-label="date filter mode"
      >
        <ToggleButton value="day">Day</ToggleButton>
        <ToggleButton value="range">Range</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>

      {value.dateMode === 'day' && (
        <TextField size={size} type="date" label="Date" InputLabelProps={{ shrink: true }}
          value={value.day} onChange={(e) => set({ day: e.target.value })} sx={{ minWidth: 150 }} />
      )}
      {value.dateMode === 'range' && (
        <>
          <TextField size={size} type="date" label="From" InputLabelProps={{ shrink: true }}
            value={value.from} onChange={(e) => set({ from: e.target.value })} sx={{ minWidth: 140 }} />
          <TextField size={size} type="date" label="To" InputLabelProps={{ shrink: true }}
            value={value.to} onChange={(e) => set({ to: e.target.value })} sx={{ minWidth: 140 }} />
        </>
      )}

      {showTech && (
        <FormControl size={size} sx={{ minWidth: 170 }}>
          <InputLabel>Technician</InputLabel>
          <Select
            label="Technician"
            value={value.tech === 'all' ? 'all' : value.tech === 'unassigned' ? 'unassigned' : String(value.tech)}
            onChange={(e) => {
              const v = e.target.value;
              set({ tech: v === 'all' ? 'all' : v === 'unassigned' ? 'unassigned' : Number(v) });
            }}
          >
            <MenuItem value="all">All technicians</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
            {technicians.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>{techLabel(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showFlags && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Tooltip title="Scheduled window passed without completion">
            <Chip icon={<MissedIcon />} label="Missed" size={size === 'small' ? 'small' : 'medium'}
              color={value.flags.missed ? 'error' : 'default'}
              variant={value.flags.missed ? 'filled' : 'outlined'}
              onClick={() => toggleFlag('missed')} clickable />
          </Tooltip>
          <Tooltip title="Incomplete - needs a return visit">
            <Chip icon={<ReturnIcon />} label="Needs return" size={size === 'small' ? 'small' : 'medium'}
              color={value.flags.needs_return ? 'warning' : 'default'}
              variant={value.flags.needs_return ? 'filled' : 'outlined'}
              onClick={() => toggleFlag('needs_return')} clickable />
          </Tooltip>
          <Tooltip title="Open past the aging threshold for its call type">
            <Chip icon={<AgedIcon />} label="Aged" size={size === 'small' ? 'small' : 'medium'}
              color={value.flags.aged ? 'secondary' : 'default'}
              variant={value.flags.aged ? 'filled' : 'outlined'}
              onClick={() => toggleFlag('aged')} clickable />
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
